import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import { Box, Typography, Grid } from "@mui/material";
import PrimaryTextField from "../../Inputs/PrimaryTextField/PrimaryTextField";
import InfoIcon from "../../../assets/icons/InfoIcon";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import TextPill from "../../Pills/TextPill/TextPill";
import EtaStatusPill from "../../Pills/EtaStatusPill/EtaStatusPill";
import LoadStatusPill from "../../Pills/LoadStatusPill/LoadStatusPill";
import StyledTooltip from "../../StyledTooltip/StyledTooltip";
import SecurityCheck from "../../SecurityCheck/SecurityCheck";
import useEtaStatus from "../../../hooks/useEtaStatus/useEtaStatus";
import { formatCurrency, formatEmpty, formatNumberWithCommas, formatTitleCase } from "../../../utils/formatHelpers";
import { parseAndFormatDate, parseMcleodDateWithOffset } from "../../../utils/dateHelpers";
import { getEtaStatus, nullCheck } from "../../../utils/dataHelpers";
import { CarrierService } from "../../../services/CarrierService";
import { AlertContext } from "../../Dashboard/Dashboard";

export default function LoadProfileHeader({
  activeLoad,
  setActiveLoad,
  onLoadFieldUpdate,
  maxPayInfo,
  callins
}) {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const [stops, setStops] = useState([]);
  const [originLocation, setOriginLocation] = useState("");
  const [originDate, setOriginDate] = useState("");
  const [destinationLocation, setDestinationLocation] = useState("");
  const [destinationDate, setDestinationDate] = useState("");
  const [extraStops, setExtraStops] = useState("");
  const [tenderFullType, setTenderFullType] = useState("");
  const [maxPay, setMaxPay] = useState(null);
  const [etaStatus, setEtaStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { currentStatus } = useEtaStatus(etaStatus);
  const [etaNextStop, setEtaNextStop] = useState("");
  const [originTextColor, setOriginTextColor] = useState("black_opacity.100");
  const [destinationTextColor, setDestinationTextColor] = useState("black_opacity.100");

  const handleUpdateMaxPay = () => {
    if (activeLoad.maxPay === maxPay) return;

    if (maxPay >= 100) {
      const payload = {
        orderId: activeLoad.id,
        movementId: activeLoad.movementId,
        overrideMaxPay: maxPay
      };

      setIsLoading(true);
      CarrierService.UpdateMaxPay(instance, payload).then(() => {
        const loadUpdated = activeLoad;
        loadUpdated.maxPay = maxPay;
        setActiveLoad(loadUpdated);
        onLoadFieldUpdate?.(activeLoad.id, "maxPay", maxPay);
        setIsLoading(false);
      }).catch(error => {
        setIsLoading(false);
        console.error(error);
        warning(error);
      });
    } else {
      setMaxPay(activeLoad.maxPay ?? 0);
      alert("Error, Minimum 3-Digits required");
    }
  };

  useEffect(() => {
    const statusColor = currentStatus?.backgroundColor || "black_opacity.100";
    if (["AVA", "COV", "COVCOM", "DIS"].includes(activeLoad.brokerageStatus)) {
      setOriginTextColor(statusColor);
    } else if (["LOA", "LOAD", "DEL"].includes(activeLoad.brokerageStatus)) {
      setDestinationTextColor(statusColor);
    }
  }, [currentStatus, activeLoad.brokerageStatus]);

  useEffect(() => {
    if (stops?.length) {
      const originStop = stops[0];
      const destinationStop = stops[stops.length - 1];

      setOriginDate(parseAndFormatDate(originStop.schedArriveEarly, "MM/dd/yy HH:mm"));
      setDestinationDate(parseAndFormatDate(destinationStop.schedArriveEarly, "MM/dd/yy HH:mm"));
      setOriginLocation(`${formatTitleCase(originStop.cityName)}, ${originStop.state}`);
      setDestinationLocation(`${formatTitleCase(destinationStop.cityName)}, ${destinationStop.state}`);
      setExtraStops(stops.length > 2 ? (`+${stops.length - 2} stops`) : "");

      if (callins?.length) {
        setEtaNextStop(parseAndFormatDate(callins[0].newEta, "MM/dd/yy HH:mm"));
        setEtaStatus(getEtaStatus(
          activeLoad.brokerageStatus,
          callins[0].newEta,
          originStop.schedArriveEarly,
          originStop.schedArriveLate,
          destinationStop.schedArriveEarly,
          destinationStop.schedArriveLate
        ));
      }
    }
  }, [stops]);

  useEffect(() => {
    if (activeLoad) {
      setStops(activeLoad.stops);
      setMaxPay(activeLoad.maxPay);

      switch (activeLoad.tender) {
        case "F":
          setTenderFullType("Fixed");
          break;
        case "S":
          setTenderFullType("Spot");
          break;
        case "V":
          setTenderFullType("Volume");
          break;
        default:
          setTenderFullType(activeLoad.tender);
      }
    }
  }, [activeLoad]);

  return (
    <Box
      sx={{
        padding: "0 20px 20px 20px",
        backgroundColor: "black_opacity.05",
        color: "black_solid.80"
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <Box>
          <Box sx={{ display: "flex", marginBottom: "5px", alignItems: "center" }}>
            <Typography variant="h1" color="black_opacity.100">
              {activeLoad.id}
            </Typography>
            <Typography variant="h1" sx={{ marginLeft: "10px", color: "black_opacity.100" }}>
              {originLocation}
            </Typography>
            <ArrowForwardIcon sx={{ marginLeft: "10px", fontSize: "30px" }} />
            <Typography variant="h1" sx={{ marginLeft: "10px", color: "black_opacity.100" }}>
              {destinationLocation}
            </Typography>
            {extraStops &&
              <StyledTooltip arrow title={stops?.map((s, i) => {
                if (i > 0 && i < stops.length - 1) return (<Typography key={s.id} variant="body1" marginBottom="5px">{`${formatTitleCase(s.cityName)}, ${s.state}`}</Typography>);
              })}>
                <Box sx={{ marginLeft: "10px" }}>
                  <TextPill value={extraStops} padding="2px 12px" />
                </Box>
              </StyledTooltip>
            }
            <Box sx={{ margin: "2px 0 0 10px" }}>
              <EtaStatusPill value={etaStatus} tooltip={etaNextStop} />
            </Box>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", color: "black_opacity.70" }}>
            <LoadStatusPill value={activeLoad.brokerageStatus} darkTheme />
            {activeLoad.revenueCodeId && (
              <>
                <Typography variant="h4" sx={{ marginX: "10px" }}>&bull;</Typography>
                <Typography variant="h4">
                  {activeLoad.revenueCodeId}
                </Typography>
              </>
            )}
            {activeLoad.miles > 0 && (
              <>
                <Typography variant="h4" sx={{ marginX: "10px" }}>&bull;</Typography>
                <Typography variant="h4">
                  {activeLoad.miles} mi
                </Typography>
              </>
            )}
            {activeLoad.weight > 0 && (
              <>
                <Typography variant="h4" sx={{ marginX: "10px" }}>&bull;</Typography>
                <Typography variant="h4">
                  {formatNumberWithCommas(activeLoad.weight)} lbs
                </Typography>
              </>
            )}
            {activeLoad.settlementDate && (
              <>
                <Typography variant="h4" sx={{ marginX: "10px" }}>&bull;</Typography>
                <TextPill value="Xferred to Settlement" padding="2px 12px" backgroundColor="warning.60" color="black_opacity.70" />
              </>
            )}
          </Box>
        </Box>
        <Box sx={{ display: "flex", marginLeft: "auto" }}>
          <Box>
            <Typography variant="subhead1" color={originTextColor}>
              Pick Up
            </Typography>
            <Typography variant="body1" color={originTextColor}>
              {nullCheck(originDate, "-")}
            </Typography>
          </Box>
          <Box sx={{ marginLeft: "10px" }}>
            <Typography variant="subhead1" color={destinationTextColor}>
              Drop Off
            </Typography>
            <Typography variant="body1" color={destinationTextColor}>
              {nullCheck(destinationDate, "-")}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="subhead1" color="black_opacity.100">
              Customer
            </Typography>
            <Typography variant="body1" color="black_opacity.100">
              {nullCheck(activeLoad.customer?.name, "-")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="subhead1" color="black_opacity.100">
              Commodity
            </Typography>
            <Typography variant="body1" color="black_opacity.100">
              {nullCheck(formatTitleCase(activeLoad.commodity), "-")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="subhead1" color="black_opacity.100">
              Tender Type
            </Typography>
            <Typography variant="body1" color="black_opacity.100">
              {nullCheck(tenderFullType, "-")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="subhead1" color="black_opacity.100">
              Date Entered
            </Typography>
            <Typography variant="body1" color="black_opacity.100">
              {parseAndFormatDate(parseMcleodDateWithOffset(activeLoad.dateEntered), "MM/dd/yyyy")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="subhead1" color="black_opacity.100">
              BOL #
            </Typography>
            <Typography variant="body1" color="black_opacity.100">
              {nullCheck(activeLoad.blnum, "-")}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="subhead1" color="black_opacity.100">
              Cargo Value
            </Typography>
            <Typography variant="body1" color="black_opacity.100">
              {formatCurrency(Math.floor(activeLoad.cargoValue))}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="subhead1" color="black_opacity.100">
              Equipment Type
            </Typography>
            <Typography variant="body1" color="black_opacity.100">
              {activeLoad.equipmentTypeId}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="subhead1" color="black_opacity.100">
              Min / Max Temp
            </Typography>
            <Typography>
              {typeof (activeLoad.minTemp) == "number" ? <span>{activeLoad.minTemp} / {activeLoad.maxTemp}</span> : "-"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="subhead1" color="black_opacity.100" display="flex">
              Max Pay
              {maxPayInfo.isRevenueAdjustment &&
                <StyledTooltip title={`DAT: ${formatEmpty(formatCurrency(maxPayInfo.datRateview, 2))}`}>
                  <Box sx={{ height: "20px", marginLeft: "5px" }}>
                    <InfoIcon
                      sx={{
                        fontSize: "20px",
                        color: "error.60",
                      }}
                    />
                  </Box>
                </StyledTooltip>
              }
            </Typography>
            <SecurityCheck
              componentName="CarrierOffersDashboardManager"
              action="Read">
              <span style={{ display: "flex", paddingRight: "20px" }}>
                <PrimaryTextField
                  value={maxPay}
                  onChange={setMaxPay}
                  onBlur={handleUpdateMaxPay}
                  isNumber
                  isCurrency
                  disabled={isLoading}
                  error={!maxPay}
                />
              </span>
            </SecurityCheck>
            <SecurityCheck
              componentName="CarrierOffersDashboardRep"
              action="Read">
              <Typography variant="body1" color="black_opacity.100">
                {formatCurrency(maxPay)}
              </Typography>
            </SecurityCheck>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <Typography variant="subhead1" color="black_opacity.100">
              Revenue
            </Typography>
            <Typography variant="body1" color="black_opacity.100">
              {formatCurrency(activeLoad.totalCharge)}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}

LoadProfileHeader.propTypes = {
  activeLoad: PropTypes.object.isRequired,
  setActiveLoad: PropTypes.func.isRequired,
  onLoadFieldUpdate: PropTypes.func,
  maxPayInfo: PropTypes.object.isRequired,
  callins: PropTypes.array.isRequired
};